import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Plus, Pencil, Trash2 } from "lucide-react";

interface Service {
  id: string;
  name: string;
  description: string;
  price: number;
  duration_minutes: number;
  available_at_home: boolean;
  available_at_studio: boolean;
  is_active: boolean;
  category: string;
}

const empty: Omit<Service, "id"> = {
  name: "", description: "", price: 0, duration_minutes: 60,
  available_at_home: true, available_at_studio: true, is_active: true, category: "massage",
};

const ServicesAdmin = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [editing, setEditing] = useState<Partial<Service> | null>(null);
  const [open, setOpen] = useState(false);
  const { toast } = useToast();

  const fetch = async () => {
    const { data } = await supabase.from("services").select("*").order("created_at");
    setServices((data as Service[]) || []);
  };

  const fetchCategories = async () => {
    const { data } = await supabase.from("app_settings").select("value").eq("key", "service_categories").single();
    if (data?.value && Array.isArray(data.value)) {
      setCategories(data.value as string[]);
    } else {
      setCategories(["Massagem", "Kids", "Estética", "Podologia"]);
    }
  };

  useEffect(() => { fetch(); fetchCategories(); }, []);

  const save = async () => {
    if (!editing?.name) return;
    if (editing.id) {
      const { id, ...rest } = editing;
      await supabase.from("services").update(rest).eq("id", id);
    } else {
      await supabase.from("services").insert(editing as any);
    }
    toast({ title: "Serviço guardado!" });
    setOpen(false);
    setEditing(null);
    fetch();
  };

  const remove = async (id: string) => {
    await supabase.from("services").delete().eq("id", id);
    toast({ title: "Serviço removido!" });
    fetch();
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="font-display text-3xl font-bold">Serviços</h1>
        <Button className="rounded-full gap-2" onClick={() => { setEditing({ ...empty }); setOpen(true); }}>
          <Plus size={16} /> Novo
        </Button>
      </div>

      <div className="space-y-3">
        {services.map((s) => (
          <div key={s.id} className="rounded-xl border border-border bg-card p-5 flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-foreground">{s.name}</h3>
              <p className="text-sm text-muted-foreground">
                €{Number(s.price).toFixed(2)} · {s.duration_minutes}min
                {s.category && <> · <span className="text-primary">{s.category}</span></>}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-xs px-2 py-0.5 rounded-full ${s.is_active ? "bg-emerald-100 text-emerald-700" : "bg-muted text-muted-foreground"}`}>
                {s.is_active ? "Ativo" : "Inativo"}
              </span>
              <Button size="icon" variant="ghost" onClick={() => { setEditing(s); setOpen(true); }}>
                <Pencil size={16} />
              </Button>
              <Button size="icon" variant="ghost" onClick={() => remove(s.id)}>
                <Trash2 size={16} />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{editing?.id ? "Editar" : "Novo"} Serviço</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div><Label>Nome</Label><Input value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
              <div><Label>Descrição</Label><Input value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></div>
              <div>
                <Label>Categoria / Setor</Label>
                <Select value={editing.category || ""} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                  <SelectTrigger><SelectValue placeholder="Selecione uma categoria" /></SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">Gerencie categorias em Colaboradores → Categorias</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Preço (€)</Label><Input type="number" value={editing.price || 0} onChange={(e) => setEditing({ ...editing, price: parseFloat(e.target.value) })} /></div>
                <div><Label>Duração (min)</Label><Input type="number" value={editing.duration_minutes || 60} onChange={(e) => setEditing({ ...editing, duration_minutes: parseInt(e.target.value) })} /></div>
              </div>
              <div className="flex items-center justify-between">
                <Label>Domicílio</Label>
                <Switch checked={editing.available_at_home} onCheckedChange={(v) => setEditing({ ...editing, available_at_home: v })} />
              </div>
              <div className="flex items-center justify-between">
                <Label>Espaço</Label>
                <Switch checked={editing.available_at_studio} onCheckedChange={(v) => setEditing({ ...editing, available_at_studio: v })} />
              </div>
              <div className="flex items-center justify-between">
                <Label>Ativo</Label>
                <Switch checked={editing.is_active} onCheckedChange={(v) => setEditing({ ...editing, is_active: v })} />
              </div>
              <Button className="w-full rounded-full" onClick={save}>Guardar</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServicesAdmin;
